import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import { Box, Text } from 'grommet';
import { useAppTheme } from '@Components/Context';
import {
  Seo,
  AppButton,
  Spinning,
} from '@Components/Control';
import {
  ProductFormDropdownSelect,
  ProductFormSingleTextInput,
} from '@Components/Partial/Product/NewSearch';
import { NoDataPlaceholder } from '@Components/Partial/Product/SharedComponents.jsx';
import { AdminNavWrapper } from '@Components/Navigation';
import { buildDateTimeLabel } from '@Helpers/util';
import {
  fetchNagraManualCandidates,
  createNagraManualCandidate,
} from '@Actions';
import paths from '@Components/configs/paths';

// ── Helpers ───────────────────────────────────────────────────────────────────
const isValidUrl = (val) => {
  if (!val?.trim()) return false;
  try { return Boolean(new URL(val.trim())); } catch { return false; }
};

// ── Constants ─────────────────────────────────────────────────────────────────

const ORIGIN_OPTIONS = ['SocialMedia', 'UGC', 'MessagingApp', 'FreeM3UList'];
const STREAM_TYPES = ['hls', 'p2p', 'rtmp'];

const NAGRA_CONTENT_TYPES = [
  'LIVE - Full Pirate Live Stream',
  'LIVE - Sports Highlights',
  'VOD - Full Pirate Stream',
  'VOD - Clip Pirate Stream',
];

const REVIEW_STATUS_COLORS = {
  pending: { bg: '#71717a22', border: '#71717a', text: '#71717a' },
  'in-review': { bg: '#3b82f622', border: '#3b82f6', text: '#3b82f6' },
  approved: { bg: '#00a85522', border: '#00a855', text: '#00a855' },
  rejected: { bg: '#e53e3e22', border: '#e53e3e', text: '#e53e3e' },
  submitted: { bg: '#8b5cf622', border: '#8b5cf6', text: '#8b5cf6' },
};

// Fields shown per origin
const ORIGIN_METADATA_FIELDS = {
  // CAT #3 LIVE SPORTS EVENT — mandatory origin: SocialMedia
  SocialMedia: [
    'contentType', 'country', 'language', 'subscribers', 'channelStatus',
    'claimType', 'viewsDownloads', 'videoDuration',
    'infringementPublishingTimestamp', 'domainName', 'serverName',
    'serverLocation', 'identificationTimestamp', 'nexguardWatermarkId',
    'searchString',
  ],
  // CAT #3 LIVE SPORTS EVENT — mandatory origin: UGC
  UGC: [
    'contentType', 'country', 'language', 'subscribers', 'channelStatus',
    'claimType', 'viewsDownloads', 'videoDuration',
    'infringementPublishingTimestamp', 'domainName', 'serverName',
    'serverLocation', 'identificationTimestamp', 'nexguardWatermarkId',
    'searchString',
  ],
  // CAT #3 LIVE SPORTS EVENT — mandatory origin: MessagingApp
  // Note: claimType is NOT in NAGRA doc v3.2 CAT #3 MessagingApp fields
  MessagingApp: [
    'contentType', 'country', 'language', 'channelType', 'channelStatus',
    'subscribers', 'viewsDownloads', 'infringementPublishingTimestamp',
    'domainName', 'serverName', 'serverLocation', 'identificationTimestamp',
    'nexguardWatermarkId', 'searchString',
  ],
  FreeM3UList: [
    'contentType', 'country', 'language', 'searchString',
    'domainName', 'serverName', 'serverLocation',
    'identificationTimestamp', 'viewsDownloads',
  ],
};

const METADATA_FIELD_LABELS = {
  subscribers: 'Subscribers',
  channelStatus: 'Channel Status',
  claimType: 'Claim Type',
  viewsDownloads: 'Views / Downloads',
  videoDuration: 'Video Duration',
  contentType: 'Content Type',
  country: 'Country',
  language: 'Language',
  infringementPublishingTimestamp: 'Publishing Timestamp',
  channelType: 'Channel Type (Public/Private)',
  searchString: 'Search String',
  domainName: 'Domain Name',
  serverName: 'Server Name',
  serverLocation: 'Server Location',
  identificationTimestamp: 'Identification Timestamp',
  nexguardWatermarkId: 'NexGuard Watermark ID',
};

const EMPTY_FORM = {
  videoUrl: '',
  referenceUrl: '',
  viewingPageUrl: '',
  playerPageUrl: '',
  type: 'hls',
  origin: '',
  channelName: '',
  playCommand: '',
  // metadata
  subscribers: '',
  channelStatus: '',
  claimType: '',
  viewsDownloads: '',
  videoDuration: '',
  contentType: '',
  country: '',
  language: '',
  infringementPublishingTimestamp: '',
  channelType: '',
  searchString: '',
  domainName: '',
  serverName: '',
  serverLocation: '',
  identificationTimestamp: '',
  nexguardWatermarkId: '',
};

// ── Sub-components ─────────────────────────────────────────────────────────────
/* eslint-disable react/prop-types */
const Section = ({ title, children, sectionBg }) => (
  <Box
    direction="column"
    gap="1rem"
    pad="1.5rem"
    round="var(--app-radius)"
    background={sectionBg}
    style={{ boxShadow: 'var(--elevation)' }}
  >
    <Text size="1rem" weight={600}>{title}</Text>
    {children}
  </Box>
);

const FieldCol = ({
  label, children, page, required,
}) => (
  <Box direction="column" gap="0.4rem">
    <Text size="0.8rem" color={page.hintText}>
      {label}
      {required && <Text color="var(--app-error)"> *</Text>}
    </Text>
    {children}
  </Box>
);

const StatusBadge = ({ value }) => {
  if (!value) return <Text size="0.85rem">—</Text>;
  const colors = REVIEW_STATUS_COLORS[value] || REVIEW_STATUS_COLORS.pending;
  return (
    <Box
      background={colors.bg}
      border={{ color: colors.border, size: '1px' }}
      round="6px"
      pad={{ vertical: '0.2rem', horizontal: '0.6rem' }}
      width="fit-content"
    >
      <Text size="0.8rem" weight={600} color={colors.text}>
        {String(value).replace(/-/g, ' ')}
      </Text>
    </Box>
  );
};
/* eslint-enable react/prop-types */

// ── Main Page ─────────────────────────────────────────────────────────────────

const AdminNagraManualSubmissionPage = ({
  small,
  location,
  manualCandidates,
  manualTotal,
  manualLoading,
  manualCreating,
  fetchManualCandidates,
  createManualCandidate,
}) => {
  const navigate = useNavigate();
  const { theme: { page, button }, isDarkMode } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;
  const useSectionBg = isDarkMode ? page.altBg : page.background;

  const [form, setForm] = React.useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = React.useState({});

  React.useEffect(() => {
    fetchManualCandidates({ source: 'manual' });
  }, []);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) setFormErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const validateForm = (f) => {
    const errors = {};
    if (!f.videoUrl?.trim()) {
      errors.videoUrl = 'Video URL is required.';
    } else if (!isValidUrl(f.videoUrl)) {
      errors.videoUrl = 'Video URL must be a valid URL (e.g. https://example.com/stream.m3u8).';
    }
    if (!f.referenceUrl?.trim()) {
      errors.referenceUrl = 'Reference URL is required.';
    } else if (!isValidUrl(f.referenceUrl)) {
      errors.referenceUrl = 'Reference URL must be a valid URL (e.g. https://formula1.com/...).';
    }
    if (!f.viewingPageUrl?.trim()) {
      errors.viewingPageUrl = 'Viewing Page URL is required.';
    } else if (!isValidUrl(f.viewingPageUrl)) {
      errors.viewingPageUrl = 'Viewing Page URL must be a valid URL (e.g. https://pirate-site.com/watch).';
    }
    if (f.playerPageUrl?.trim() && !isValidUrl(f.playerPageUrl)) {
      errors.playerPageUrl = 'Player Page URL must be a valid URL.';
    }
    if (!f.type) {
      errors.type = 'Stream type is required. Please select one from the list.';
    }
    if (!f.origin) {
      errors.origin = 'Origin is required. Please select the platform type where this stream was found.';
    }
    if (f.type === 'rtmp' && !f.playCommand?.trim()) {
      errors.playCommand = 'A play command is required for RTMP streams (e.g. ffplay rtmp://...).';
    }
    return errors;
  };

  const originMetaFields = ORIGIN_METADATA_FIELDS[form.origin] ?? [];

  const isFormReady = Boolean(
    form.videoUrl?.trim()
    && form.referenceUrl?.trim()
    && form.viewingPageUrl?.trim()
    && form.type
    && form.origin
    && (form.type !== 'rtmp' || form.playCommand?.trim()),
  );

  const handleSubmit = () => {
    const errors = validateForm(form);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});

    /* eslint-disable camelcase */
    const body = {
      video_url: { url: form.videoUrl.trim() },
      reference_url: { url: form.referenceUrl.trim() },
      viewing_page_url: { url: form.viewingPageUrl.trim() },
      ...(form.playerPageUrl?.trim()
        ? { player_page_url: { url: form.playerPageUrl.trim() } }
        : {}),
      type: form.type,
      origin: form.origin,
      ...(form.channelName?.trim() ? { channel_name: form.channelName.trim() } : {}),
      ...(form.type === 'rtmp' && form.playCommand?.trim()
        ? { play_command: form.playCommand.trim() }
        : {}),
    };

    // Explicit overrides for fields where naive camelCase→snake_case is wrong
    const SNAKE_OVERRIDES = {
      nexguardWatermarkId: 'nexguard_watermark_id',
    };

    const metadata = {};
    originMetaFields.forEach((field) => {
      const snakeField = SNAKE_OVERRIDES[field]
        ?? field.replace(/([A-Z])/g, '_$1').toLowerCase();
      if (form[field]?.trim?.()) {
        metadata[snakeField] = form[field].trim();
      }
    });
    if (Object.keys(metadata).length > 0) body.metadata = metadata;
    /* eslint-enable camelcase */

    createManualCandidate(body, (candidate) => {
      // Navigate to detail page for screenshot upload + final review
      /* eslint-disable-next-line no-underscore-dangle */
      navigate(paths.adminNagraCandidateDetail.replace(':id', candidate.id ?? candidate._id));
    });
  };

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Social Media & IPTV Submissions',
        subTitle: 'Nagra Monitoring',
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem', horizontal: small ? '1rem' : '2rem' }}
        gap="1.5rem"
        width="100%"
      >
        {/* ── Submission Form ──────────────────────────────────────────────── */}
        <Box direction={small ? 'column' : 'row'} gap="1.5rem" align="stretch">

          {/* Left: Core fields */}
          <Box flex={{ grow: 3, shrink: 1 }} style={{ minWidth: 0 }} direction="column" gap="1.5rem">
            <Section title="Stream Information" sectionBg={useSectionBg}>
              <FieldCol label="Video URL" page={page} required>
                <ProductFormSingleTextInput
                  value={form.videoUrl}
                  handleFormValues={(val) => handleChange('videoUrl', val)}
                  placeholder="https://..."
                  error={!!formErrors.videoUrl}
                />
                {formErrors.videoUrl && (
                  <Text size="0.75rem" color="var(--app-error)">{formErrors.videoUrl}</Text>
                )}
              </FieldCol>
              <FieldCol label="Reference URL" page={page} required>
                <ProductFormSingleTextInput
                  value={form.referenceUrl}
                  handleFormValues={(val) => handleChange('referenceUrl', val)}
                  placeholder="https://..."
                  error={!!formErrors.referenceUrl}
                />
                {formErrors.referenceUrl && (
                  <Text size="0.75rem" color="var(--app-error)">{formErrors.referenceUrl}</Text>
                )}
              </FieldCol>
              <FieldCol label="Viewing Page URL" page={page} required>
                <ProductFormSingleTextInput
                  value={form.viewingPageUrl}
                  handleFormValues={(val) => handleChange('viewingPageUrl', val)}
                  placeholder="https://..."
                  error={!!formErrors.viewingPageUrl}
                />
                {formErrors.viewingPageUrl && (
                  <Text size="0.75rem" color="var(--app-error)">{formErrors.viewingPageUrl}</Text>
                )}
              </FieldCol>
              <FieldCol label="Player Page URL (optional)" page={page}>
                <ProductFormSingleTextInput
                  value={form.playerPageUrl}
                  handleFormValues={(val) => handleChange('playerPageUrl', val)}
                  placeholder="https://..."
                  error={!!formErrors.playerPageUrl}
                />
                {formErrors.playerPageUrl && (
                  <Text size="0.75rem" color="var(--app-error)">{formErrors.playerPageUrl}</Text>
                )}
              </FieldCol>
              <Box direction={small ? 'column' : 'row'} gap="1rem">
                <Box flex direction="column" gap="0.25rem">
                  <FieldCol label="Stream Type" page={page} required>
                    <ProductFormDropdownSelect
                      selectable
                      simpleOptions={STREAM_TYPES}
                      keepLabel
                      value={form.type}
                      handleFormValues={(val) => handleChange('type', val)}
                    />
                  </FieldCol>
                  {formErrors.type && (
                    <Text size="0.75rem" color="var(--app-error)">{formErrors.type}</Text>
                  )}
                </Box>
                <Box flex direction="column" gap="0.25rem">
                  <FieldCol label="Origin" page={page} required>
                    <ProductFormDropdownSelect
                      selectable
                      simpleOptions={ORIGIN_OPTIONS}
                      keepLabel
                      value={form.origin}
                      handleFormValues={(val) => handleChange('origin', val)}
                    />
                  </FieldCol>
                  {formErrors.origin && (
                    <Text size="0.75rem" color="var(--app-error)">{formErrors.origin}</Text>
                  )}
                </Box>
              </Box>
              <FieldCol label="Channel Name (optional)" page={page}>
                <ProductFormSingleTextInput
                  value={form.channelName}
                  handleFormValues={(val) => handleChange('channelName', val)}
                />
              </FieldCol>
              {form.type === 'rtmp' && (
                <FieldCol label="Play Command" page={page} required>
                  <ProductFormSingleTextInput
                    value={form.playCommand}
                    handleFormValues={(val) => handleChange('playCommand', val)}
                    placeholder="ffplay rtmp://..."
                    error={!!formErrors.playCommand}
                  />
                  {formErrors.playCommand && (
                    <Text size="0.75rem" color="var(--app-error)">{formErrors.playCommand}</Text>
                  )}
                </FieldCol>
              )}
            </Section>
          </Box>

          {/* Right: Origin-specific metadata */}
          <Box flex={{ grow: 2, shrink: 1 }} style={{ minWidth: 0 }} direction="column" gap="1.5rem">
            <Section title="Metadata" sectionBg={useSectionBg}>
              {!form.origin && (
                <Text size="0.85rem" color={page.hintText}>
                  Select an origin to see relevant metadata fields.
                </Text>
              )}
              {form.origin && (
                <Text size="0.78rem" color={page.hintText}>
                  All metadata fields are optional. NAGRA accepts and stores
                  every field listed here — fill in as many as you have available.
                </Text>
              )}
              {originMetaFields.map((field) => {
                const label = METADATA_FIELD_LABELS[field] ?? field;
                if (field === 'contentType') {
                  return (
                    <FieldCol key={field} label={label} page={page}>
                      <ProductFormDropdownSelect
                        selectable
                        simpleOptions={['', ...NAGRA_CONTENT_TYPES]}
                        keepLabel
                        value={form[field]}
                        handleFormValues={(val) => handleChange(field, val)}
                      />
                    </FieldCol>
                  );
                }
                return (
                  <FieldCol key={field} label={label} page={page}>
                    <ProductFormSingleTextInput
                      value={form[field]}
                      handleFormValues={(val) => handleChange(field, val)}
                    />
                  </FieldCol>
                );
              })}
            </Section>

            {/* Submit button */}
            <Box direction="column" gap="0.5rem">
              {Object.keys(formErrors).length > 0 && (
                <Text size="0.8rem" color="var(--app-error)">
                  Some required fields are missing or invalid. Please review the highlighted fields and try again.
                </Text>
              )}
              <AppButton
                overrideHover
                level="normal"
                label={manualCreating ? 'Creating...' : 'Create & Review'}
                disabled={manualCreating || !isFormReady}
                bgColor={button.primaryBg}
                color={button.primaryText}
                onClick={handleSubmit}
              />
              <Text size="0.75rem" color={page.hintText}>
                Creates the record and opens the detail page for screenshot upload and final review.
              </Text>
            </Box>
          </Box>
        </Box>

        {/* ── Manual Submissions Table ─────────────────────────────────────── */}
        <Section title="Manual Submissions" sectionBg={useSectionBg}>
          {manualLoading && (
            <Box align="center" pad="large">
              <Spinning size="medium" color={page.highlight} />
            </Box>
          )}

          {!manualLoading && manualCandidates.length === 0 && (
            <NoDataPlaceholder
              loading={false}
              label="No manual submissions yet."
              textColor={page.primaryText}
              loaderColor={page.highlight}
            />
          )}

          {!manualLoading && manualCandidates.length > 0 && (
            <>
              <Text size="0.8rem" color={page.hintText}>
                {manualTotal}
                {' total record'}
                {manualTotal !== 1 ? 's' : ''}
              </Text>
              <Box direction="column" overflow={{ horizontal: 'auto' }}>
                {/* Header */}
                <Box
                  direction="row"
                  pad={{ horizontal: '0.75rem', vertical: '0.5rem' }}
                  border={{ color: page.tableBorder, size: '1px' }}
                  round={{ corner: 'top', size: '6px' }}
                  background={page.tableBg}
                  gap="1rem"
                >
                  {['Viewing Page URL', 'Video URL', 'Origin', 'Channel', 'Status', 'Created', ''].map((col) => (
                    <Box key={col} flex={col !== ''} width={col === '' ? '80px' : undefined}>
                      <Text size="0.78rem" weight={600} color={page.secondaryText}>{col}</Text>
                    </Box>
                  ))}
                </Box>

                {/* Rows */}
                {manualCandidates.map((c, idx) => (
                  <Box
                    key={c.id ?? idx}
                    direction="row"
                    pad={{ horizontal: '0.75rem', vertical: '0.6rem' }}
                    border={[
                      { color: page.tableBorder, size: '1px', side: 'left' },
                      { color: page.tableBorder, size: '1px', side: 'right' },
                      { color: page.tableBorder, size: '1px', side: 'bottom' },
                    ]}
                    background={page.background}
                    gap="1rem"
                    align="center"
                  >
                    <Box flex>
                      <Text size="0.8rem" color={page.tableText} truncate>
                        {c.viewingPageUrl?.url ?? '—'}
                      </Text>
                    </Box>
                    <Box flex>
                      <Text size="0.8rem" color={page.tableText} truncate>
                        {c.videoUrl?.url ?? '—'}
                      </Text>
                    </Box>
                    <Box flex>
                      <Text size="0.8rem" color={page.tableText}>{c.origin ?? '—'}</Text>
                    </Box>
                    <Box flex>
                      <Text size="0.8rem" color={page.tableText}>{c.channelName ?? '—'}</Text>
                    </Box>
                    <Box flex>
                      <StatusBadge value={c.reviewStatus} />
                    </Box>
                    <Box flex>
                      <Text size="0.8rem" color={page.tableText}>
                        {c.createdAt ? buildDateTimeLabel(c.createdAt) : '—'}
                      </Text>
                    </Box>
                    <Box flex={false} style={{ flexShrink: 0 }}>
                      <AppButton
                        overrideHover
                        level="tableControls"
                        label="View"
                        bgColor={isDarkMode ? button.primaryBg : page.altBg}
                        color={isDarkMode ? button.primaryText : page.primaryText}
                        onClick={() => {
                          /* eslint-disable-next-line no-underscore-dangle */
                          navigate(paths.adminNagraCandidateDetail.replace(':id', c.id ?? c._id));
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Section>
      </Box>
    </AdminNavWrapper>
  );
};

AdminNagraManualSubmissionPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  manualCandidates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  manualTotal: PropTypes.number.isRequired,
  manualLoading: PropTypes.bool.isRequired,
  manualCreating: PropTypes.bool.isRequired,
  fetchManualCandidates: PropTypes.func.isRequired,
  createManualCandidate: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  return {
    manualCandidates: state.nagraCandidates.manualCandidates,
    manualTotal: state.nagraCandidates.manualTotal,
    manualLoading: state.nagraCandidates.manualLoading,
    manualCreating: state.nagraCandidates.manualCreating,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchManualCandidates: fetchNagraManualCandidates,
      createManualCandidate: createNagraManualCandidate,
    },
    dispatch,
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(AdminNagraManualSubmissionPage);
